/**
 * Task-specific Column Visibility Menu
 *
 * Thin wrapper around the generic ColumnVisibilityMenu from common/DataTable.
 * Injects task-specific display name and grouping functions.
 */

import {
  ColumnVisibilityMenu as GenericColumnVisibilityMenu,
  TableColumn,
} from '@/components/common/DataTable';

import {
  getColumnDisplayName,
  groupColumnsByCategory,
} from '../../config/columnConfig';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface ColumnVisibilityMenuProps<TData> {
  /** All available columns from the column factory */
  allColumns: TableColumn<TData>[];
  /** Currently visible column IDs in display order */
  visibleColumns: string[];
  /** Callback when a column is toggled */
  onToggleColumn: (columnId: string) => void;
  /** Callback when columns are reordered (receives new full order) */
  onReorderColumns: (newOrder: string[]) => void;
  /** Callback to reset to default layout */
  onReset: () => void;
  /** Whether the current layout differs from default */
  hasChanges: boolean;
  /** Custom label for the trigger button */
  label?: string;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export const ColumnVisibilityMenu = <TData,>({
  allColumns,
  visibleColumns,
  onToggleColumn,
  onReorderColumns,
  onReset,
  hasChanges,
  label,
}: ColumnVisibilityMenuProps<TData>) => {
  return (
    <GenericColumnVisibilityMenu
      allColumns={allColumns}
      visibleColumns={visibleColumns}
      onToggleColumn={onToggleColumn}
      onReorderColumns={onReorderColumns}
      onReset={onReset}
      hasChanges={hasChanges}
      getDisplayName={getColumnDisplayName}
      groupByCategory={groupColumnsByCategory}
      label={label}
    />
  );
};
